import {Fragment, useEffect, useState} from 'react'
import {Dialog, DialogPanel, DialogTitle, Transition, TransitionChild} from '@headlessui/react'
import {useAtom} from "jotai/index";
import {collectionDialogAtom, collectionsAtom} from "../pages/Application/Storage";
import {useMutation, useQuery} from "@tanstack/react-query";
import {loadCollectionsForAdvertisement, updateCollectionsForAdvertisement} from "../../api";
import {CollectionMutation} from "../../models/CollectionMutation";
import {CollectionDialogData} from "../../models/CollectionDialogData";
import {NULL_UUID} from "../../constants";

export default function CollectionDialog() {
    const [dialogStore, setDialogStore] = useAtom(collectionDialogAtom);

    const [collections] = useAtom(collectionsAtom);

    const [containingCollections, setContainingCollections] = useState(new Array<string>());

    const {data: existingContainingCollections, isSuccess: containingCollectionsLoaded} = useQuery({
        queryKey: [dialogStore.advertisementID],
        queryFn: () => loadCollectionsForAdvertisement(dialogStore.advertisementID),
        placeholderData: [],
        retry: 0,
        enabled: !!dialogStore.advertisementID && dialogStore.advertisementID !== NULL_UUID
    });

    const updateCollection = useMutation({
        mutationFn: (mutationParams: CollectionMutation) => {
            return updateCollectionsForAdvertisement(mutationParams.id, mutationParams.collectionIDs);
        }
    })

    useEffect(() => {
        setContainingCollections(existingContainingCollections || []);
    }, [existingContainingCollections]);

    const close = () => {
        setDialogStore((draft: CollectionDialogData) => {
            draft.open = false;
        });
    }

    return (
        <Transition show={dialogStore.open} as={Fragment}>
            <Dialog className="relative z-10" onClose={close}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                </TransitionChild>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div>
                                    <div className="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3"
                                                     className="text-2xl font-semibold leading-6 text-gray-900">
                                            Collection Quick Edit
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-lg pb-4">
                                                Please check each collection you want this advertisement to be included
                                                in.
                                            </p>
                                            <hr/>

                                            {containingCollectionsLoaded &&
                                                <fieldset className="mt-12 text-left">
                                                    <legend
                                                        className="text-base font-semibold leading-6 text-gray-900">Your
                                                        Collections
                                                    </legend>
                                                    <div
                                                        className="mt-4 divide-y divide-gray-200 border-b border-t border-gray-200">
                                                        {collections.map((collection, idx) => (
                                                            <div key={idx} className="relative flex items-start py-4">
                                                                <div className="min-w-0 flex-1 text-sm leading-6">
                                                                    <label htmlFor={`collection-${collection.id}`}
                                                                           className="select-none font-medium text-gray-900">
                                                                        {collection.name}
                                                                    </label>
                                                                </div>
                                                                <div className="ml-3 flex h-6 items-center">
                                                                    <input
                                                                        id={`collection-${collection.id}`}
                                                                        name={`collection-${collection.id}`}
                                                                        type="checkbox"
                                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                                        checked={containingCollections.includes(collection.id)}
                                                                        onChange={(e) => {
                                                                            let updated = [...containingCollections];

                                                                            if (e.target.checked) {
                                                                                updated.push(collection.id)
                                                                            } else {
                                                                                updated = updated.filter(id => id !== collection.id);
                                                                            }

                                                                            setContainingCollections(updated);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </fieldset>
                                            }

                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                                        onClick={() => {
                                            updateCollection.mutateAsync({
                                                id: dialogStore.advertisementID,
                                                collectionIDs: containingCollections
                                            }).then(() => close())
                                        }}>
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                        onClick={close}>
                                        Cancel
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
